import React from 'react';
import {
    BulletIcon,
    ListItem,
    TextLabel,
    Typography,
    Link,
    Tooltip,
    TooltipHandler
} from '@genestack/ui';
import classNames from 'classnames';
import styles from './right-panel.module.css';
import {isSelectMultipleMouseEvent} from '../../../../../utils/get-client-os';
import {HighlightedText} from '../../../../../components/highlighted-text';
import {ViewDeclarationButton} from './statistics/view-declaration-button';

interface Props {
    label: string;
    count?: number;
    padding: 2 | 3;
    searchString?: string;
    append?: React.ReactNode;
    declarationUrl?: string;
}

interface InteractiveProps extends Props {
    graphViewNodeIds: number[];
    active: boolean;
    selectGraphNodes: (nodes: number[], isSelectMultiple: boolean) => void;
    setHoveredNodes: (nodeIds: number[]) => void;
}

function isInteractiveProps(props: Props | InteractiveProps): props is InteractiveProps {
    return !!(props as InteractiveProps).selectGraphNodes;
}

export function RightPanelListItem(props: Props | InteractiveProps) {
    const isInteractiveItem = isInteractiveProps(props);
    const onMouseOver = React.useCallback(
        () => isInteractiveItem && props.setHoveredNodes(props.graphViewNodeIds),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isInteractiveItem]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onMouseOut = React.useCallback(() => isInteractiveItem && props.setHoveredNodes([]), []);
    const onClick = React.useCallback(
        (e: React.MouseEvent) => {
            if (isInteractiveItem) {
                props.selectGraphNodes(props.graphViewNodeIds, isSelectMultipleMouseEvent(e));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isInteractiveItem && props.selectGraphNodes]
    );

    const LabelComponent = isInteractiveItem ? Link : Typography;

    return (
        <ListItem
            interactive={isInteractiveItem}
            className={classNames(
                styles.statItem,
                isInteractiveItem && !props.active && styles.statItemUnselected,
                props.padding === 2 ? styles.statItemLevel2 : styles.statItemLevel3
            )}
            onClick={isInteractiveItem ? onClick : undefined}
            active={isInteractiveItem && props.active}
            onMouseOver={isInteractiveItem ? onMouseOver : undefined}
            onMouseOut={isInteractiveItem ? onMouseOut : undefined}
            append={props.append}
        >
            <TextLabel
                caption={
                    <div className={styles.listItemCaption}>
                        {props.count === undefined ? undefined : (
                            <Typography intent="quiet" box="inline">
                                <BulletIcon />
                                {props.count}
                            </Typography>
                        )}

                        {props.declarationUrl && (
                            <ViewDeclarationButton declarationUrl={props.declarationUrl} />
                        )}
                    </div>
                }
            >
                <TooltipHandler
                    tooltip={<Tooltip className={styles.labelTooltip}>{props.label}</Tooltip>}
                >
                    <LabelComponent>
                        <HighlightedText text={props.label} value={props.searchString || ''} />
                    </LabelComponent>
                </TooltipHandler>
            </TextLabel>
        </ListItem>
    );
}
